import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { MEETING_TITLE_MAX, type Meeting, type MeetingPerson, type Project } from '@jisy/shared';
import { ApiRequestError } from '../api/client.js';
import {
  fathomConnectionQuery,
  meetingsQuery,
  projectsQuery,
  useMeetingArchiveMutation,
  useMeetingSectionMutation,
  useMeetingTitleMutation,
} from '../api/queries.js';
import { SectionSelect } from '../components/SectionSelect.js';
import { TableSkeleton } from '../components/States.js';
import { formatShortDate } from '../lib/format.js';
import { localeTag } from '../i18n/index.js';
import styles from './MeetingsPage.module.css';

/**
 * Встречи из Fathom.
 *
 * Данные идут напрямую из Fathom, а не из кэша Jisy: сортировки и поиска здесь нет, поэтому
 * проекция в PostgreSQL не дала бы ничего, кроме расхождений. Отсюда и особенности — страницы
 * листаются курсором Fathom, а размер страницы выбирает он сам.
 *
 * Ключ личный, поэтому раздел у каждого свой; пока ключа нет, вместо таблицы стоит объяснение
 * и дорога в настройки — единственное место, где ключ вводится.
 *
 * Тот же экран работает вкладкой направления: с `projectId` показываются только встречи этого
 * раздела, а место заголовка занимает переключатель вида раздела — он приходит через `leading`.
 */
export function MeetingsPage({
  onOpenSettings,
  projectId = null,
  leading,
}: {
  onOpenSettings: () => void;
  projectId?: string | null;
  leading?: React.ReactNode;
}): React.ReactElement {
  const { t } = useTranslation();

  /** Архив — отдельный режим списка, как «Завершённые» в таблице задач, а не фильтр поверх. */
  const [archived, setArchived] = useState(false);

  const connection = useQuery(fathomConnectionQuery);
  const connected = connection.data?.connected === true;
  const meetings = useInfiniteQuery(meetingsQuery(connected, archived, projectId));
  // Тот же список разделов, что в боковой колонке: цвет метки и порядок должны совпадать.
  const projects = useQuery(projectsQuery);
  const setSection = useMeetingSectionMutation();
  const setArchivedFlag = useMeetingArchiveMutation();
  const setTitle = useMeetingTitleMutation();

  const rows = useMemo(
    () => meetings.data?.pages.flatMap((page) => page.items) ?? [],
    [meetings.data],
  );

  const error = meetings.error instanceof ApiRequestError ? meetings.error : null;

  return (
    <main className={styles.main}>
      <div className={styles.toolbar}>
        {leading ?? <h1 className={styles.title}>{t('meetings.title')}</h1>}
        <div className={styles.spacer} />
        {connected && (
          <button
            type="button"
            className={archived ? styles.toggleActive : styles.toggle}
            onClick={() => setArchived((value) => !value)}
            aria-pressed={archived}
            title={t('meetings.archiveHint')}
          >
            {t('meetings.archive')}
          </button>
        )}
        {connected && (
          <button
            type="button"
            className={styles.refresh}
            onClick={() => void meetings.refetch()}
            disabled={meetings.isFetching}
            title={t('meetings.refreshHint')}
          >
            {t('meetings.refresh')}
          </button>
        )}
      </div>

      {/* Ключа нет или он больше не действует — оба состояния ведут в настройки, но по разным
          причинам, и подменять одно другим значило бы врать про исправный ключ. */}
      {connection.data && !connected && (
        <div className={styles.notice}>
          <div className={styles.noticeTitle}>
            {t(connection.data.revoked ? 'meetings.revokedTitle' : 'meetings.connectTitle')}
          </div>
          <p className={styles.noticeBody}>
            {t(connection.data.revoked ? 'meetings.revokedBody' : 'meetings.connectBody')}
          </p>
          <button type="button" className={styles.noticeAction} onClick={onOpenSettings}>
            {t('meetings.openSettings')}
          </button>
        </div>
      )}

      {error && (
        <div className={styles.error} role="alert">
          <span>{t(`errors.${error.code}`)}</span>
          {error.requestId && (
            <span className={`mono ${styles.requestId}`}>req {error.requestId.slice(0, 8)}</span>
          )}
        </div>
      )}

      {connected && meetings.isPending && <TableSkeleton rows={6} />}

      {connected && !meetings.isPending && rows.length === 0 && !error && (
        <div className={styles.notice}>
          <div className={styles.noticeTitle}>
            {t(
              archived
                ? 'meetings.emptyArchiveTitle'
                : projectId === null
                  ? 'meetings.emptyTitle'
                  : 'meetings.emptySectionTitle',
            )}
          </div>
          <p className={styles.noticeBody}>
            {t(
              archived
                ? 'meetings.emptyArchiveBody'
                : projectId === null
                  ? 'meetings.emptyBody'
                  : 'meetings.emptySectionBody',
            )}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <MeetingsTable
          meetings={rows}
          projects={projects.data?.projects ?? []}
          onChangeSection={(meetingId, section) =>
            setSection.mutate({ meetingId, projectId: section })
          }
          onRename={(meetingId, title) => setTitle.mutate({ meetingId, title })}
          onToggleArchived={(meetingId, next) =>
            setArchivedFlag.mutate({ meetingId, archived: next })
          }
          hasMore={meetings.hasNextPage}
          loadingMore={meetings.isFetchingNextPage}
          onLoadMore={() => {
            if (meetings.hasNextPage && !meetings.isFetchingNextPage) void meetings.fetchNextPage();
          }}
        />
      )}

      <div className={styles.footer}>
        <span className={styles.footerText}>
          {rows.length > 0 ? t('meetings.loaded', { count: rows.length }) : ''}
        </span>
      </div>
    </main>
  );
}

function MeetingsTable({
  meetings,
  projects,
  onChangeSection,
  onRename,
  onToggleArchived,
  hasMore,
  loadingMore,
  onLoadMore,
}: {
  meetings: Meeting[];
  projects: Project[];
  onChangeSection: (meetingId: string, projectId: string | null) => void;
  onRename: (meetingId: string, title: string) => void;
  onToggleArchived: (meetingId: string, archived: boolean) => void;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  /** Какую строку сейчас переименовывают: правка идёт по месту, окна для одного поля не нужно. */
  const [renamingId, setRenamingId] = useState<string | null>(null);

  // Бесконечная прокрутка — та же, что в таблице задач: конец списка вошёл в вид, значит пора.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadMore();
      },
      { root: scrollRef.current, rootMargin: '200px' },
    );

    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [hasMore, onLoadMore, meetings.length]);

  return (
    <div className={styles.wrap} ref={scrollRef} role="table" aria-label={t('meetings.title')}>
      <div className={styles.head} role="row">
        {(
          ['date', 'title', 'section', 'type', 'duration', 'participants', 'recordedBy'] as const
        ).map((column) => (
          <span className={styles.headCell} role="columnheader" key={column}>
            {t(`meetings.columns.${column}`)}
          </span>
        ))}
        {/* Колонка действия: заголовка у неё нет — подпись над одной иконкой лишняя. */}
        <span className={styles.headCell} role="columnheader" aria-label={t('meetings.archive')} />
      </div>

      <div className={styles.body} role="rowgroup">
        {meetings.map((meeting) => (
          <MeetingRow
            meeting={meeting}
            projects={projects}
            onChangeSection={onChangeSection}
            renaming={meeting.id === renamingId}
            onStartRename={() => setRenamingId(meeting.id)}
            onRename={(title) => {
              setRenamingId(null);
              if (title !== meeting.title) onRename(meeting.id, title);
            }}
            onCancelRename={() => setRenamingId(null)}
            onToggleArchived={onToggleArchived}
            key={meeting.id}
          />
        ))}

        <div className={styles.sentinel} ref={sentinelRef}>
          {loadingMore && <span className={styles.loadingMore}>{t('table.loadingMore')}</span>}
        </div>
      </div>
    </div>
  );
}

/**
 * Строка ведёт на запись в Fathom: у встречи ровно одно осмысленное действие, и заводить ради
 * него карточку внутри Jisy незачем — содержимое всё равно живёт там.
 *
 * Ссылка стоит на названии, а не на строке целиком: строка — ячейки таблицы, и якорь вокруг них
 * отнял бы у экранного диктора и заголовки колонок, и разбиение на ячейки. Щелчок по любому
 * месту строки открывает то же самое — это удобство мыши, а не единственный способ.
 */
function MeetingRow({
  meeting,
  projects,
  onChangeSection,
  renaming,
  onStartRename,
  onRename,
  onCancelRename,
  onToggleArchived,
}: {
  meeting: Meeting;
  projects: Project[];
  onChangeSection: (meetingId: string, projectId: string | null) => void;
  renaming: boolean;
  onStartRename: () => void;
  onRename: (title: string) => void;
  onCancelRename: () => void;
  onToggleArchived: (meetingId: string, archived: boolean) => void;
}): React.ReactElement {
  const { t } = useTranslation();

  const href = meeting.url ?? meeting.shareUrl;
  const title = meeting.title || t('meetings.untitled');
  const participants = peopleOf(meeting);

  return (
    <div
      className={href ? `${styles.row} ${styles.rowClickable}` : styles.row}
      role="row"
      onClick={() => {
        if (href) window.open(href, '_blank', 'noopener,noreferrer');
      }}
    >
      <span className={`mono ${styles.cellDate}`} role="cell">
        {formatShortDate(meeting.startedAt)}
        <span className={styles.cellTime}>{formatTime(meeting.startedAt)}</span>
      </span>
      {/*
        Название правится по месту. В Fathom оно не уходит: её внешний API отдаёт встречи только
        на чтение — поэтому у переименованной строки рядом стоит значок с исходным названием,
        чтобы запись можно было найти и там.
      */}
      <span className={styles.cellTitle} role="cell" onClick={(event) => event.stopPropagation()}>
        {renaming ? (
          <TitleEdit value={meeting.title} onDone={onRename} onCancel={onCancelRename} />
        ) : (
          <>
            {href ? (
              <a
                className={`${styles.titleLink} truncate`}
                href={href}
                target="_blank"
                rel="noreferrer noopener"
                title={title}
              >
                {title}
              </a>
            ) : (
              <span className="truncate" title={title}>
                {title}
              </span>
            )}
            {meeting.renamed && (
              <span
                className={styles.renamedMark}
                title={t('meetings.renamedHint', {
                  title: meeting.sourceTitle || t('meetings.untitled'),
                })}
                aria-label={t('meetings.renamedHint', {
                  title: meeting.sourceTitle || t('meetings.untitled'),
                })}
              >
                <TagIcon />
              </span>
            )}
            <button
              type="button"
              className={styles.renameButton}
              onClick={onStartRename}
              title={t('meetings.renameHint')}
              aria-label={t('meetings.rename')}
            >
              <PencilIcon />
            </button>
          </>
        )}
      </span>
      {/* Раздел — единственная правимая ячейка: выбор уходит на сервер сразу, без режима правки. */}
      <span className={styles.cellSection} role="cell">
        <SectionSelect
          projects={projects}
          value={meeting.projectId}
          onChange={(projectId) => onChangeSection(meeting.id, projectId)}
        />
      </span>
      <span className={`${styles.cellMuted} truncate`} role="cell">
        {meeting.meetingType ?? ''}
      </span>
      <span className={`mono ${styles.cellDuration}`} role="cell">
        {/* «мин» не склоняется ни в одном из четырёх языков — множественного числа здесь нет. */}
        {meeting.durationMinutes === null
          ? '—'
          : t('meetings.minutes', { value: meeting.durationMinutes })}
      </span>
      {/*
        Имена, а не счёт: Fathom возвращает приглашённых из календаря, и у встреч без
        приглашения список короткий — число там говорит куда меньше, чем сами люди.
      */}
      <span
        className={`${styles.cellPeople} truncate`}
        role="cell"
        title={participants.map(fullNameOf).join('\n')}
      >
        {participants.length === 0 ? '—' : participants.map(nameOf).join(', ')}
      </span>
      <span className={`${styles.cellMuted} truncate`} role="cell">
        {meeting.recordedBy ? nameOf(meeting.recordedBy) : '—'}
      </span>
      <span className={styles.cellAction} role="cell">
        <button
          type="button"
          className={styles.archiveButton}
          title={t(meeting.archived ? 'meetings.unarchive' : 'meetings.archiveRow')}
          aria-label={t(meeting.archived ? 'meetings.unarchive' : 'meetings.archiveRow')}
          onClick={(event) => {
            // Иначе вместе с архивацией открылась бы сама запись.
            event.stopPropagation();
            onToggleArchived(meeting.id, !meeting.archived);
          }}
        >
          {meeting.archived ? <UnarchiveIcon /> : <ArchiveIcon />}
        </button>
      </span>
    </div>
  );
}

/**
 * Поле названия. Enter сохраняет, Esc отменяет, уход фокуса — тоже сохраняет: это правка одной
 * строки, и отдельная кнопка «Сохранить» рядом с ней была бы лишним движением. Пустая строка
 * возвращает название из Fathom — так снимается переименование.
 */
function TitleEdit({
  value,
  onDone,
  onCancel,
}: {
  value: string;
  onDone: (title: string) => void;
  onCancel: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);

  return (
    <input
      className={styles.titleInput}
      value={draft}
      autoFocus
      maxLength={MEETING_TITLE_MAX}
      aria-label={t('meetings.rename')}
      title={t('meetings.renameHint')}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => onDone(draft.trim())}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          onDone(draft.trim());
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          onCancel();
        }
      }}
    />
  );
}

/**
 * Бирка: название дано в Jisy. Форма нарочно другая, чем у кнопки правки, — рядом стоят два
 * значка, и одинаковые карандаши читались бы как одна кнопка, нажатая дважды.
 */
function TagIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
      <path
        d="M2.5 7.4V2.5h4.9l6 6-4.9 4.9z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <circle cx="5" cy="5" r="1" fill="currentColor" />
    </svg>
  );
}

/** Карандаш: кнопка правки названия. */
function PencilIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
      <path
        d="M11.2 2.8a1.5 1.5 0 0 1 2.1 2.1L5.6 12.6l-2.8.7.7-2.8z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Архив: короб с крышкой. */
function ArchiveIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path
        d="M2.5 5.5h11v7a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1v-7zM2 2.5h12v3H2z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M6.5 8.5h3" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

/** Возврат из архива: тот же короб со стрелкой наружу. */
function UnarchiveIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path
        d="M2.5 5.5h11v7a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1v-7zM2 2.5h12v3H2z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path
        d="M8 11.5V8m0 0L6.5 9.5M8 8l1.5 1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Люди на встрече: приглашённые из календаря плюс тот, кто записал. Записавший в списке не лишний
 * — он на встрече был, — но повторяться не должен, поэтому совпадения снимаются по почте,
 * а при её отсутствии по имени.
 */
function peopleOf(meeting: Meeting): MeetingPerson[] {
  const seen = new Set<string>();
  const people: MeetingPerson[] = [];

  for (const person of [...meeting.invitees, meeting.recordedBy]) {
    if (!person) continue;
    const key = (person.email ?? person.name ?? '').toLowerCase();
    if (key === '' || seen.has(key)) continue;
    seen.add(key);
    people.push(person);
  }

  return people;
}

function nameOf(person: MeetingPerson): string {
  return person.name ?? person.email ?? '';
}

/** Для всплывающей подсказки: имя и почта, по человеку на строку. */
function fullNameOf(person: MeetingPerson): string {
  if (person.name && person.email) return `${person.name} · ${person.email}`;
  return nameOf(person);
}

/** Время встречи рядом с датой: в один день их бывает несколько, и час их различает. */
function formatTime(value: string | null): string {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(localeTag(), { hour: '2-digit', minute: '2-digit' }).format(date);
}
